/**
 * Supervisão (spec 071): o atendimento das conversas, "Agora" e por "Período".
 * Quem usa é o supervisor, com a página aberta ao lado das conversas. A
 * pergunta de cada bloco, nesta ordem:
 *  1. tem cliente esperando?
 *  2. alguém está travado ou sumiu com conversa na mão?
 *  3. como foi o dia (ou o mês)?
 *
 * Chama-se "Supervisão" e não "Atendimento": no layout Simplificado,
 * "Atendimentos" já é o nome dos tickets.
 */
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { attendanceNowQuery } from './supervision-queries';
import { SupervisionNow } from './supervision-now';
import { SupervisionPeriod } from './supervision-period';
import {
  supervisionSearchSchema,
  type SupervisionSearch,
  type SupervisionTab,
} from './supervision-search';

export { supervisionSearchSchema, type SupervisionSearch };

const TAB_LABELS: Record<SupervisionTab, string> = { agora: 'Agora', periodo: 'Período' };

export function SupervisionPage() {
  const search = supervisionSearchSchema.parse(useSearch({ strict: false }));
  const navigate = useNavigate();
  const setSearch = (patch: Partial<SupervisionSearch>) =>
    void navigate({ to: '/supervisao', search: { ...search, ...patch }, replace: true });
  const deskIds = search.mesas && search.mesas.length > 0 ? search.mesas : undefined;
  // as mesas do filtro vêm do monitor (o mesmo recorte de quem olha), também na aba Período
  const desksQ = useQuery({
    ...attendanceNowQuery(undefined),
    select: (d) => d.desks,
    staleTime: 5 * 60_000,
  });
  const desks = desksQ.data ?? [];

  const toggleDesk = (id: number) => {
    const cur = new Set(search.mesas ?? []);
    if (cur.has(id)) cur.delete(id);
    else cur.add(id);
    setSearch({ mesas: [...cur] });
  };

  return (
    <TooltipProvider>
      <div className="mx-auto flex max-w-6xl flex-col gap-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-foreground">Supervisão</h1>
            <p className="text-[13px] text-muted-foreground">
              As conversas da equipe: a fila, quem atende o quê e os tempos.
            </p>
          </div>
          <div
            className="flex items-center gap-0.5 rounded-md bg-muted p-0.5"
            role="tablist"
            aria-label="Visão"
          >
            {(Object.keys(TAB_LABELS) as SupervisionTab[]).map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={search.aba === tab}
                onClick={() => setSearch({ aba: tab })}
                className={cn(
                  'h-8 rounded-[5px] px-4 text-[13px] font-medium transition-colors',
                  search.aba === tab
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {TAB_LABELS[tab]}
              </button>
            ))}
          </div>
        </div>

        {desks.length > 1 ? (
          <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Mesas">
            <span className="mr-1 text-[13px] text-muted-foreground">Mesas:</span>
            <DeskChip active={!deskIds} onClick={() => setSearch({ mesas: [] })}>
              Todas
            </DeskChip>
            {desks.map((d) => (
              <DeskChip
                key={d.id}
                active={deskIds?.includes(d.id) ?? false}
                onClick={() => toggleDesk(d.id)}
              >
                {d.name}
              </DeskChip>
            ))}
          </div>
        ) : null}

        {search.aba === 'agora' ? (
          <SupervisionNow deskIds={deskIds} />
        ) : (
          <SupervisionPeriod
            search={search}
            deskIds={deskIds}
            onPeriod={(start, end) => setSearch({ inicio: start, fim: end })}
          />
        )}
      </div>
    </TooltipProvider>
  );
}

function DeskChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'h-7 rounded-full border px-3 text-[13px] whitespace-nowrap transition-colors',
        active
          ? 'border-primary bg-primary/10 font-medium text-primary-pressed'
          : 'border-border bg-card text-muted-foreground hover:text-foreground'
      )}
    >
      {children}
    </button>
  );
}
